import { Send, TrendingUp } from "@mui/icons-material";
import { Box, Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { formatPrice } from "@/components/common/PriceInput";
import { CardItem } from "@/components/Delivery/CreateDeliveryDeal/ui/card-item";
import CustomCard from "@/components/Delivery/CreateDeliveryDeal/ui/custom-card";
import { calculateMargin, getMaxAllowedDriverPrice } from "@/utils/delivery-pricing";

import type { DeliveryDealFormApi } from "./hooks/use-delivery-deal-form";

type DeliveryPricingSectionProps = {
  form: DeliveryDealFormApi;
  onClientPriceChange: (clientPrice: number) => void;
};

export function DeliveryPricingSection({ form, onClientPriceChange }: DeliveryPricingSectionProps) {
  const theme = useTheme();

  return (
    <Stack spacing={3}>
      <CustomCard label="Assignation du chauffeur">
        <form.AppField name="selectedDriverId">
          {(field) => <field.DriverAutocomplete />}
        </form.AppField>
      </CustomCard>

      <CustomCard label="Prix proposé au client">
        <form.AppField
          name="clientFinalPrice"
          listeners={{
            onChange: (value) => onClientPriceChange(value.value),
          }}
        >
          {(field) => (
            <field.PriceField
              min={0}
              suffix="€"
              sx={{
                "& .MuiOutlinedInput-input": {
                  py: 2,
                  fontWeight: "bold",
                  fontSize: "1.2em",
                  color: "primary.main",
                },
              }}
            />
          )}
        </form.AppField>
      </CustomCard>

      <CustomCard label="Assignation du chauffeur">
        <form.Subscribe
          selector={(state) => ({
            minPrice: state.values.minPrice,
            medianPrice: state.values.medianPrice,
            maxPrice: state.values.maxPrice,
            clientFinalPrice: state.values.clientFinalPrice,
            selectedDriverId: state.values.selectedDriverId,
          })}
        >
          {({ minPrice, medianPrice, maxPrice, clientFinalPrice, selectedDriverId }) => (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 4 }}>
                  <CardItem label="Min" icon="📉">
                    <Typography variant="body1" fontWeight="bold">
                      {formatPrice(minPrice) || "0.00"}€
                    </Typography>
                  </CardItem>
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <CardItem label="Médiane" icon="📊">
                    <Typography variant="body1" fontWeight="bold">
                      {formatPrice(medianPrice) || "0.00"}€
                    </Typography>
                  </CardItem>
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <CardItem label="Max" icon="📈">
                    <form.AppField
                      name="maxPrice"
                      listeners={{
                        onChange: ({ value: nextValue }) => {
                          const threshold = getMaxAllowedDriverPrice(clientFinalPrice);
                          const clampedValue =
                            clientFinalPrice > 0 ? Math.min(nextValue, threshold) : nextValue;
                          form.setFieldValue("medianPrice", clampedValue);
                        },
                      }}
                    >
                      {(field) => (
                        <field.PriceField
                          min={minPrice + 0.01}
                          max={
                            clientFinalPrice > 0
                              ? getMaxAllowedDriverPrice(clientFinalPrice)
                              : undefined
                          }
                          disabled={clientFinalPrice <= 0}
                          variant="standard"
                          sx={{ "& input": { fontWeight: "bold", fontSize: "0.95rem" } }}
                        />
                      )}
                    </form.AppField>
                  </CardItem>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ mt: 3 }}>
                <form.AppField name="wantedPrice">
                  {(field) => (
                    <field.PricingSliderField
                      min={minPrice}
                      max={maxPrice}
                      label="Prix recommandé"
                      driverSelected={Boolean(selectedDriverId)}
                    />
                  )}
                </form.AppField>
              </Box>
            </>
          )}
        </form.Subscribe>
      </CustomCard>

      <form.Subscribe
        selector={(state) => ({
          canSubmit: state.canSubmit,
          isSubmitting: state.isSubmitting,
          clientFinalPrice: state.values.clientFinalPrice,
          wantedPrice: state.values.wantedPrice,
        })}
      >
        {({ canSubmit, isSubmitting, clientFinalPrice, wantedPrice }) => {
          const margin = calculateMargin(clientFinalPrice, wantedPrice);
          const marginColor = margin < 0.55 ? "#ff9800" : "#4caf50";

          return (
            <Box sx={{ position: "relative", mb: 2 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: -1,
                  zIndex: 2,
                  minWidth: 140,
                  height: 32,
                  backgroundColor: marginColor,
                  borderTopRightRadius: 8,
                  borderBottomLeftRadius: 16,
                  px: 1.5,
                  py: 0.5,
                  boxShadow: `0 2px 8px ${alpha(marginColor, 0.4)}`,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    right: 0,
                    width: 0,
                    height: 0,
                    borderLeft: "8px solid transparent",
                    borderTop: `8px solid ${marginColor}`,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                  <TrendingUp sx={{ fontSize: 14, color: "white" }} />
                  <Typography
                    variant="caption"
                    textAlign="center"
                    sx={{
                      fontWeight: 700,
                      color: "white",
                      fontSize: "0.7rem",
                      letterSpacing: 0.3,
                    }}
                  >
                    {(margin * 100).toFixed(1)}%
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "white",
                      fontSize: "0.65rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {margin < 0.55 ? "Prudent" : "Bon"}
                  </Typography>
                </Stack>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="outlined"
                disabled={!canSubmit || isSubmitting}
                startIcon={<Send sx={{ fontSize: 20 }} />}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 600,
                  border: `1px solid ${theme.palette.primary.main}`,
                  color: theme.palette.primary.main,
                  position: "relative",
                  overflow: "visible",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    letterSpacing: 0.5,
                  }}
                >
                  Envoyer le deal
                </Typography>
              </Button>
            </Box>
          );
        }}
      </form.Subscribe>
    </Stack>
  );
}
